import React, { memo, useEffect } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ConfigInfoCard } from 'src/components/scheduler/detail/ConfigInfoCard'
import { EditActionConfigForm } from 'src/components/scheduler/detail/EditActionConfigForm'
import { PaymentInfoCard } from 'src/components/scheduler/detail/PaymentInfoCard'
import { BackButton } from 'src/components/shared/BackButton'
import { Button } from 'src/components/shared/Button'
import { ContentCard } from 'src/components/shared/ContentCard'
import { GSIcon } from 'src/components/shared/GSIcon'
import { SectionCard } from 'src/components/shared/SectionCard'
import { SectionCardSkeleton } from 'src/components/shared/SectionCardSkeleton'
import { AdminFetchRetryConfig } from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { ADMIN_ROUTES, buildScheduledActionRetriesPath } from 'src/constants/routes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { useHasPermission } from 'src/hooks/useHasPermission'
import { addS } from 'src/utils/baseUtils'
import {
  getRetryConfigDetail,
  getRetryConfigDetailError,
  getRetryConfigDetailLoading,
} from 'src/selectors/retryConfig'

/**
 * A scheduled action's config, payment and retry history as its own page, reached by clicking a
 * row of the Partners scheduled actions tab.
 *
 * Fetched BY ID, never plucked from the list: `GET /:configId` exists, so a deep link works, and
 * the list row (`RetryConfigListRow`) is a different shape from this detail entity anyway.
 * (Contrast `CustomerInvoiceDetailsPage`, which has no by-id endpoint and has to search the first
 * page — that workaround is not needed here.)
 */
const ScheduledActionDetailPageComponent = () => {
  const { configId } = useParams<{ configId: string }>()
  const [searchParams] = useSearchParams()

  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const detail = useSelector(getRetryConfigDetail)
  const isLoading = useSelector(getRetryConfigDetailLoading)
  const detailError = useSelector(getRetryConfigDetailError)

  // PUT …/retry-configs/:configId (edit) and …/retry-jobs/:id (reschedule). Without it the page
  // shows the info cards only (ENG-1207 part 2).
  const canManageSchedulers = useHasPermission(InternalPermission.SchedulersManage)

  useEffect(() => {
    if (!isAuthenticated || !configId) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchRetryConfig.request({ configId }))
  }, [asyncDispatchWithErrorToast, configId, isAuthenticated])

  // The list's filters (tab, partner, page) rode along in the query string, so Back restores
  // the same view rather than a reset list. The retries page carries them on too, so the whole
  // trail back to the filtered list survives.
  const listPath = {
    pathname: ADMIN_ROUTES.SCHEDULERS_MANAGEMENT_URL,
    search: searchParams.toString(),
  }

  const retriesPath = {
    pathname: configId ? buildScheduledActionRetriesPath(configId) : '',
    search: searchParams.toString(),
  }

  return (
    <div
      data-testid="scheduled-action-detail-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col gap-6 overflow-y-auto p-6`}
    >
      <ContentCard className={tw`flex flex-col gap-4`}>
        <BackButton
          label="Back to scheduled actions"
          to={listPath}
          dataTestId="scheduled-action-back-button"
        />
        <Typography variant="h2">Scheduled action detail</Typography>
      </ContentCard>

      {isLoading && !detail.config ? <SectionCardSkeleton /> : null}

      {detailError ? (
        <SectionCard title="Scheduled action">
          <Typography variant="body2" css={tw`text-critical-500`}>
            {detailError}
          </Typography>
        </SectionCard>
      ) : null}

      {/*
        Two columns: the config and the form that edits it stack in the left one, so a field and
        its current value stay in the same eyeline; Payment — which is read-only and unrelated to
        that form — takes the right. `items-start` so the taller column does not stretch the
        other's card backgrounds. Falls back to one column under `lg`, where two columns of
        label/value pairs would each be too narrow to read.
      */}
      <div css={tw`grid grid-cols-1 items-start gap-6 lg:grid-cols-2`}>
        <div css={tw`flex min-w-0 flex-col gap-6`}>
          <ConfigInfoCard config={detail.config} />
          {configId && canManageSchedulers ? (
            <EditActionConfigForm config={detail.config} configId={configId} />
          ) : null}
        </div>
        <PaymentInfoCard payment={detail.payment} />
      </div>

      {/*
        The retry jobs live on their own page rather than a third card here: the table plus its
        reschedule form is a screen's worth of interaction on its own, and it pushed everything
        else out of view. The count comes from the already-loaded detail, so the button can say
        how much is behind it before the user commits to the navigation.
      */}
      {configId ? (
        <SectionCard
          title="Retries history"
          subtitle={`${detail.actionJobs.length} ${addS(detail.actionJobs.length, 'retry job')}`}
          actions={
            <Link to={retriesPath} css={tw`no-underline`}>
              <Button
                size="small"
                color="grey"
                rightIcon={
                  <GSIcon iconName="gs_chevron_right_small" size={20} css={tw`text-shades-1000`} />
                }
                data-testid="scheduled-action-open-retries-button"
              >
                Open retries history
              </Button>
            </Link>
          }
        >
          <Typography variant="body2" css={tw`text-shades-600`}>
            {canManageSchedulers
              ? 'Inspect every retry attempt for this action and reschedule a pending job.'
              : 'Inspect every retry attempt for this action.'}
          </Typography>
        </SectionCard>
      ) : null}
    </div>
  )
}

export const ScheduledActionDetailPage = memo(ScheduledActionDetailPageComponent)
